/**
 * Real Dads Chatbot Widget
 * A self-contained chat widget for therealdads.com Shopify store
 * Version 1.0 — therealdads.com
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIG & CONSTANTS
  // ============================================================================

  const CONFIG = window.RD_CHAT_CONFIG || {
    brandName: 'Real Dads',
    websiteUrl: 'https://therealdads.com',
    wholesaleUrl: 'https://therealdads.faire.com',
    productsJsonUrl: '/assets/rd-products.json',
    jokesJsonUrl: '/assets/rd-dad-jokes.json',
    freeShipThreshold: 75,
    nudgeCooldownMs: 86400000,
    bogoEnabled: true,
    bogoLabel: 'Buy 2 shirts, get a cap free',
    hatsCollectionUrl: 'https://therealdads.com/collections/hats',
    accountUrl: '/account',
    accountLoginUrl: '/account/login',
    customerLoggedIn: false,
    customerAccountsEnabled: false,
    ga4PropertyId: 'G-XXXXXXXXXX',   // Set via RD_CHAT_CONFIG or gtag on page
    analytics: true,
    leadCapture: true,
    idleNudgeDelay: 30000,
    maxHistoryVisible: 6
  };

  const COLORS = {
    white: '#FFFFFF',
    black: '#1A1A1A',
    offWhite: '#F5F0EB',
    lightGray: '#E8E8E8',
    darkGray: '#666666',
    accent: '#1A1A1A'
  };

  const NAVIGATION = {
    shop: { label: 'Shop', url: CONFIG.websiteUrl },
    about: { label: 'About', url: CONFIG.websiteUrl + '/pages/about' },
    contact: { label: 'Contact', url: CONFIG.websiteUrl + '/pages/contact' },
    faq: { label: 'FAQ', url: CONFIG.websiteUrl + '/pages/faq' },
    account: { label: 'Account', url: CONFIG.accountUrl },
    account_login: { label: 'Account Login', url: CONFIG.accountLoginUrl },
    wholesale: { label: 'Wholesale', url: CONFIG.wholesaleUrl }
  };

  // Intent priority order
  const INTENTS = {
    PRODUCT_INQUIRY: 1,
    NEW_ARRIVALS: 2,
    ORDER_STATUS: 3,
    CUSTOMER_SERVICE: 4,
    NAVIGATION: 5,
    BRAND_QUESTION: 6,
    WHOLESALE: 7,
    DAD_JOKE: 8,
    GREETING: 9,
    SIZING: 10,
    LEAD_CAPTURE: 11,
    THANKS: 12,
    FALLBACK: 13
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const STATE = {
    isOpen: false,
    sessionId: null,
    messageCount: 0,
    started: false,
    usedJokes: new Set(),
    hasShownNudge: false,
    brand: null,
    cart: null,
    cartFetchedAt: 0,
    ui: null,
    products: [],
    jokes: [],
    messages: []
  };

  // ============================================================================
  // UTILITIES
  // ============================================================================

  const Utils = {
    /**
     * Generate or retrieve session ID from localStorage
     */
    getOrCreateSessionId() {
      let sessionId = localStorage.getItem('rd_chat_session_id');
      if (!sessionId) {
        sessionId = 'rd_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('rd_chat_session_id', sessionId);
      }
      return sessionId;
    },

    /**
     * Save chat state to localStorage
     */
    saveState() {
      const state = {
        messageCount: STATE.messageCount,
        started: STATE.started,
        messages: STATE.messages,
        usedJokes: Array.from(STATE.usedJokes),
        timestamp: Date.now()
      };
      localStorage.setItem('rd_chat_state', JSON.stringify(state));
    },

    /**
     * Load chat state from localStorage
     */
    loadState() {
      const saved = localStorage.getItem('rd_chat_state');
      if (saved) {
        try {
          const state = JSON.parse(saved);
          STATE.messageCount = state.messageCount || 0;
          STATE.started = state.started || false;
          STATE.messages = state.messages || [];
          STATE.usedJokes = new Set(state.usedJokes || []);
          return true;
        } catch (e) {
          console.error('Failed to load chat state:', e);
          return false;
        }
      }
      return false;
    },

    /**
     * Sanitize user input to prevent XSS
     */
    sanitizeText(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Convert URLs in text to clickable links (after sanitizing)
     */
    linkify(sanitizedHtml) {
      return sanitizedHtml.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;">$1</a>'
      );
    },

    /**
     * Clean and normalize response text
     */
    cleanReply(text) {
      if (!text) return '';
      return text
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/\n\n+/g, '\n');
    },

    /**
     * Format cents as a USD price string.
     */
    formatMoney(cents) {
      const amount = Math.max(Number(cents || 0), 0) / 100;
      const wholeAmount = Number.isInteger(amount);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: wholeAmount ? 0 : 2,
        maximumFractionDigits: wholeAmount ? 0 : 2
      }).format(amount);
    },

    /**
     * Return the cart free-shipping threshold in cents.
     */
    getFreeShipThresholdCents() {
      return Math.round(Number(CONFIG.freeShipThreshold || 75) * 100);
    },

    /**
     * Return live free-shipping status for a cart value.
     */
    getFreeShippingStatus(subtotalCents = 0) {
      const thresholdCents = Utils.getFreeShipThresholdCents();
      const currentCents = Math.max(Number(subtotalCents || 0), 0);
      const remainingCents = Math.max(thresholdCents - currentCents, 0);
      return {
        subtotalCents: currentCents,
        thresholdCents,
        remainingCents,
        unlocked: remainingCents === 0,
        thresholdLabel: Utils.formatMoney(thresholdCents),
        remainingLabel: Utils.formatMoney(remainingCents),
        isNudgeBand: currentCents >= 5500 && currentCents < thresholdCents
      };
    },

    /**
     * Read JSON from localStorage.
     */
    getStoredJson(key, fallback) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (_e) {
        return fallback;
      }
    },

    /**
     * Write JSON to localStorage.
     */
    setStoredJson(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (_e) {}
    },

    /**
     * Check whether a nudge trigger is outside its cooldown window.
     */
    canFireNudge(triggerKey) {
      const nudges = Utils.getStoredJson('rd_chat_nudges', {});
      const lastFiredAt = Number(nudges[triggerKey] || 0);
      return !lastFiredAt || (Date.now() - lastFiredAt) > Number(CONFIG.nudgeCooldownMs || 86400000);
    },

    /**
     * Record a nudge trigger in localStorage.
     */
    markNudgeFired(triggerKey) {
      const nudges = Utils.getStoredJson('rd_chat_nudges', {});
      nudges[triggerKey] = Date.now();
      Utils.setStoredJson('rd_chat_nudges', nudges);
    },

    /**
     * Detect touch-capable devices.
     */
    isTouchDevice() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    },

    /**
     * Suppress chat nudges on checkout-like pages.
     */
    isCheckoutPage() {
      const path = window.location.pathname || '';
      return path.startsWith('/checkouts') || path.startsWith('/checkout') || Boolean(window.Shopify?.Checkout);
    },

    /**
     * Calculate page scroll depth as a 0-1 ratio.
     */
    getScrollDepth() {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll <= 0) return 0;
      return Math.min(window.scrollY / maxScroll, 1);
    },

    /**
     * Detect carrier tracking numbers and build a direct tracking URL.
     */
    detectTrackingInfo(text) {
      const normalized = text.trim();
      const upsMatch = normalized.match(/\b1Z[0-9A-Z]{16}\b/i);
      if (upsMatch) {
        const trackingNumber = upsMatch[0].toUpperCase().replace(/\s+/g, '');
        return {
          trackingNumber,
          carrier: 'UPS',
          trackingUrl: `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`
        };
      }

      const uspsMatch = normalized.match(/\b(9[2345]\d{20,24}|420\d{20,30})\b/);
      if (uspsMatch) {
        return {
          trackingNumber: uspsMatch[0],
          carrier: 'USPS',
          trackingUrl: `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(uspsMatch[0])}`
        };
      }

      const fedexMatch = normalized.match(/\b(\d{12,15}|\d{20,22})\b/);
      if (fedexMatch) {
        return {
          trackingNumber: fedexMatch[0],
          carrier: 'FedEx',
          trackingUrl: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(fedexMatch[0])}`
        };
      }

      return null;
    },

    /**
     * Extract an order number from freeform text.
     */
    extractOrderNumber(text) {
      const hashMatch = text.match(/#\s?(\d{3,})\b/);
      if (hashMatch) return `#${hashMatch[1]}`;

      const orderMatch = text.match(/\border(?:\s*(?:number|no\.?|#))?\s*[:\-]?\s*(\d{3,})\b/i);
      if (orderMatch) return `#${orderMatch[1]}`;

      return null;
    },

    /**
     * Extract an email address from freeform text.
     */
    extractEmail(text) {
      const match = text.match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i);
      return match ? match[0] : null;
    },

    /**
     * Fetch JSON file with error handling
     */
    async fetchJson(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return await response.json();
      } catch (e) {
        console.warn(`Failed to load ${url}:`, e);
        return null;
      }
    },

    /**
     * Detect intent from user message
     */
    detectIntent(message) {
      const msg = message.toLowerCase().trim();

      // New arrivals patterns (check BEFORE general product inquiry)
      if (/\b(new|newest|latest|just dropped|new drop|new arrival|new release|what.?s new|recent|just added|fresh)\b/.test(msg)) {
        return INTENTS.NEW_ARRIVALS;
      }

      // Order status patterns (check early so order help wins over navigation/help)
      if (Utils.detectTrackingInfo(message) || /\b(where('?s| is) my order|track(ing)?( my)? order|tracking number|order status|check( on)? my order|package status|shipping update|delivery status|shipment status)\b/.test(msg)) {
        return INTENTS.ORDER_STATUS;
      }

      // Customer service patterns
      if (/\b(return|returns|exchange|exchanges|refund|refunds|cancel|cancellation|change address|address change|wrong item|damaged|defective|support|customer service|shipping question|delivery issue|help with my order)\b/.test(msg)) {
        return INTENTS.CUSTOMER_SERVICE;
      }

      // Product inquiry patterns (expanded plurals)
      if (/\b(tee|tees|hat|hats|hoodie|hoodies|shorts|polo|polos|crewneck|crewnecks|shirt|shirts|collection|tennis club|what do you sell|what do you have|show me|looking for|product|products|item|items|merch|apparel|clothing|jacket|jackets|cap|caps|sweatshirt)\b/.test(msg)) {
        return INTENTS.PRODUCT_INQUIRY;
      }

      // Wholesale patterns (HIGH PRIORITY — check before navigation)
      if (/\b(wholesale|bulk|team order|club order|resale|stock|carry your|boutique|buy in bulk|large order|faire)\b/.test(msg)) {
        return INTENTS.WHOLESALE;
      }

      // Brand question patterns (check BEFORE navigation to prevent "about" collision)
      if (/\b(what is real dads|about the brand|who are you|what.?s this about|tell me about (the |real|your)|what does real dads|your story|mission|values|the brand)\b/.test(msg)) {
        return INTENTS.BRAND_QUESTION;
      }

      // Navigation patterns (trimmed to avoid colliding with brand questions)
      if (/\b(where (can|do)|find|shop\b|collections|contact|faq|account|login|sign in|help me find|take me to|link to|browse|navigate)\b/.test(msg)) {
        return INTENTS.NAVIGATION;
      }

      // Dad joke patterns
      if (/\b(joke|dad joke|make me laugh|funny|tell me|another one|one more|laugh)\b/.test(msg)) {
        return INTENTS.DAD_JOKE;
      }

      // Sizing patterns
      if (/\b(size|sizing|fit|true to size|large|medium|small|xl|xxl|xs|s|m|l|xl|what size|size chart|how does it fit)\b/.test(msg)) {
        return INTENTS.SIZING;
      }

      // Lead capture patterns
      if (/\b(notify|email|updates|new drops|subscribe|mailing list|newsletter|keep me posted|let me know when)\b/.test(msg)) {
        return INTENTS.LEAD_CAPTURE;
      }

      // Greeting patterns
      if (/\b(hi|hello|hey|what.?s up|sup|yo|good morning|good afternoon|greetings|welcome)\b/.test(msg)) {
        return INTENTS.GREETING;
      }

      // Thanks/goodbye patterns
      if (/\b(thanks|thank you|bye|goodbye|later|peace|appreciate|good bye|see ya|take care)\b/.test(msg)) {
        return INTENTS.THANKS;
      }

      return INTENTS.FALLBACK;
    },

    /**
     * Get random item from array
     */
    random(arr) {
      return arr[Math.floor(Math.random() * arr.length)];
    }
  };

  // ============================================================================
  // ANALYTICS
  // ============================================================================

  const Analytics = {
    /**
     * Log event to GA4 + custom window events + localStorage
     */
    log(eventName, data = {}) {
      if (!CONFIG.analytics) return;

      const event = {
        name: eventName,
        timestamp: Date.now(),
        sessionId: STATE.sessionId,
        page_path: window.location.pathname,
        ...data
      };

      // Log to console in dev mode
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com')) {
        console.log('[RD Chat]', eventName, data);
      }

      // Fire GA4 events via gtag()
      try {
        if (typeof gtag === 'function') {
          gtag('event', eventName, {
            event_category: data.event_category || 'chatbot',
            event_label: data.event_label || eventName,
            chat_session_id: STATE.sessionId,
            ...data
          });
        }
      } catch (_e) {}

      // Fire custom window event for external listeners
      window.dispatchEvent(new CustomEvent('rd-chat-event', { detail: event }));

      // Store analytics in localStorage
      try {
        const key = 'rd_chat_analytics';
        const stored = JSON.parse(localStorage.getItem(key) || '{}');
        stored[eventName] = (stored[eventName] || 0) + 1;
        localStorage.setItem(key, JSON.stringify(stored));
      } catch (_e) {}
    }
  };

  // ============================================================================
  // PAGE CONTEXT — detects Shopify page type for contextual responses
  // ============================================================================

  const PageContext = {
    /**
     * Detect current Shopify page type
     */
    detectPageType() {
      const path = window.location.pathname;
      if (path === '/' || path === '') return 'home';
      if (path.startsWith('/products/')) return 'product';
      if (path.startsWith('/collections/')) return 'collection';
      if (path.startsWith('/pages/')) return 'page';
      if (path.startsWith('/cart')) return 'cart';
      return 'other';
    },

    /**
     * Build page context object
     */
    build() {
      const path = window.location.pathname;
      const heading = document.querySelector('h1');
      return {
        current_url: window.location.href,
        path: path,
        title: document.title || '',
        heading: heading ? heading.textContent.trim() : '',
        page_type: PageContext.detectPageType(),
        productHandle: path.startsWith('/products/') ? path.replace('/products/', '').split('?')[0] : '',
        collectionHandle: path.startsWith('/collections/') ? path.replace('/collections/', '').split('?')[0] : '',
        visited_at: new Date().toISOString(),
      };
    },

    /**
     * Get the current page context (cached per page load)
     */
    current: null,
    get() {
      if (!PageContext.current) {
        PageContext.current = PageContext.build();
      }
      return PageContext.current;
    }
  };

  // ============================================================================
  // CART + OFFERS
  // ============================================================================

  const CartService = {
    /**
     * Find the theme cart drawer dialog using stable theme structure first, test id second.
     */
    getDrawerElement() {
      return document.querySelector('cart-drawer-component dialog.cart-drawer__dialog')
        || document.querySelector('dialog.cart-drawer__dialog')
        || document.querySelector('[data-testid="cart-drawer-dialog"]');
    },

    /**
     * Fetch the live storefront cart.
     */
    async fetch(force = false) {
      const cacheAgeMs = Date.now() - STATE.cartFetchedAt;
      if (!force && STATE.cart && cacheAgeMs < 15000) {
        return STATE.cart;
      }

      try {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        STATE.cart = await response.json();
        STATE.cartFetchedAt = Date.now();
        return STATE.cart;
      } catch (e) {
        console.warn('Failed to load cart.js:', e);
        return STATE.cart;
      }
    },

    /**
     * Return the cart value that should be compared against the free-shipping threshold.
     * This mirrors the theme progress bar, which uses `cart.total_price`.
     */
    getSubtotalCents(cart = STATE.cart) {
      if (!cart) return 0;
      return Number(cart.total_price ?? cart.items_subtotal_price ?? 0);
    },

    /**
     * Detect whether the theme cart drawer is open.
     */
    isDrawerOpen() {
      const drawer = CartService.getDrawerElement();
      return Boolean(drawer && drawer.hasAttribute('open'));
    }
  };

  const Offers = {
    /**
     * Determine whether a product is shirt-like for BXGY messaging.
     */
    isShirtProduct(product) {
      if (!product) return false;
      const category = String(product.category || '').toLowerCase();
      const tags = (product.tags || []).map(tag => String(tag).toLowerCase());
      return category === 'tops' || tags.some(tag => ['shirt', 'shirts', 'tee', 'tees', 't-shirt', 'long sleeve'].includes(tag));
    },

    /**
     * Choose a complementary collection URL for "complete the look" nudges.
     */
    getCompleteTheLookUrl(product) {
      if (Offers.isShirtProduct(product)) {
        return CONFIG.hatsCollectionUrl || `${CONFIG.websiteUrl}/collections/hats`;
      }
      return NAVIGATION.tennis_club?.url || CONFIG.websiteUrl;
    }
  };

  // ============================================================================
  // NUDGE MANAGER
  // ============================================================================

  const Nudges = {
    /**
     * Show a short toast message if the trigger is eligible.
     */
    show(message, options = {}) {
      const {
        triggerKey = 'generic',
        pageType = PageContext.get().page_type,
        analyticsData = {}
      } = options;

      if (!message || STATE.hasShownNudge || STATE.isOpen || Utils.isCheckoutPage() || !Utils.canFireNudge(triggerKey)) {
        return false;
      }

      const nudge = document.createElement('div');
      nudge.className = 'rd-toast';
      nudge.textContent = message;
      nudge.setAttribute('role', 'button');
      nudge.tabIndex = 0;
      const openChat = () => {
        if (STATE.ui) {
          UI.open(STATE.ui.widget, STATE.ui.bubble);
          STATE.ui.widget.querySelector('.rd-input-field')?.focus();
        }
        nudge.remove();
      };
      nudge.addEventListener('click', openChat);
      nudge.addEventListener('keypress', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openChat();
        }
      });

      document.body.appendChild(nudge);
      STATE.hasShownNudge = true;
      Utils.markNudgeFired(triggerKey);
      Analytics.log('nudge_fired', {
        page_type: pageType,
        trigger_key: triggerKey,
        ...analyticsData
      });

      setTimeout(() => nudge.remove(), 5000);
      return true;
    },

    /**
     * Fire a product-page fit/shipping nudge.
     */
    maybeProductNudge(triggerKey = 'product_context') {
      return Nudges.show('Questions about fit or shipping? I can help.', {
        triggerKey,
        pageType: 'product'
      });
    },

    /**
     * Fire a free-shipping nudge when the cart is in the target range.
     */
    async maybeFreeShipNudge(triggerKey = 'cart_free_ship', pageType = 'cart') {
      const cart = await CartService.fetch(true);
      const status = Utils.getFreeShippingStatus(CartService.getSubtotalCents(cart));
      if (!status.isNudgeBand) return false;

      const shown = Nudges.show(`You're ${status.remainingLabel} from free shipping — want a quick add-on?`, {
        triggerKey,
        pageType,
        analyticsData: {
          remaining_cents: status.remainingCents,
          subtotal_cents: status.subtotalCents
        }
      });

      if (shown) {
        Analytics.log('freeship_nudge', {
          page_type: pageType,
          remaining_cents: status.remainingCents,
          subtotal_cents: status.subtotalCents
        });
      }

      return shown;
    },

    /**
     * Register timer, scroll, drawer, and exit-intent nudges.
     */
    init() {
      if (Utils.isCheckoutPage()) return;

      const pageType = PageContext.get().page_type;

      if (pageType === 'product') {
        setTimeout(() => {
          Nudges.maybeProductNudge('product_timer');
        }, 22000);

        const handleScroll = () => {
          if (STATE.hasShownNudge) {
            window.removeEventListener('scroll', handleScroll);
            return;
          }
          if (Utils.getScrollDepth() >= 0.4) {
            Nudges.maybeProductNudge('product_scroll');
            window.removeEventListener('scroll', handleScroll);
          }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
      }

      if (pageType === 'cart') {
        setTimeout(() => {
          Nudges.maybeFreeShipNudge('cart_page_band', 'cart');
        }, 1800);
      }

      const drawer = CartService.getDrawerElement();
      if (drawer instanceof HTMLElement) {
        const observer = new MutationObserver(() => {
          if (drawer.hasAttribute('open')) {
            setTimeout(() => {
              Nudges.maybeFreeShipNudge('cart_drawer_band', 'cart');
            }, 1200);
          }
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }

      if (!Utils.isTouchDevice()) {
        document.addEventListener('mouseout', (event) => {
          if (!(event instanceof MouseEvent) || event.clientY > 0 || STATE.hasShownNudge || STATE.isOpen) return;
          const cartLike = pageType === 'cart' || CartService.isDrawerOpen();
          if (cartLike) {
            Nudges.maybeFreeShipNudge('cart_exit_intent', 'cart');
            return;
          }
          if (pageType === 'product' || pageType === 'collection') {
            Nudges.show('Need a quick answer on shipping or sizing before you go?', {
              triggerKey: `${pageType}_exit_intent`,
              pageType
            });
          }
        });
      }
    }
  };

  // ============================================================================
  // DOM BUILDER & CSS INJECTION
  // ============================================================================

  const DOM = {
    /**
     * Inject all CSS into the page
     */
    injectStyles() {
      const styleId = 'rd-chatbot-styles';
      if (document.getElementById(styleId)) return;

      const css = `
        /* Chat Widget Container */
        #rd-chatbot-widget {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 380px;
          height: 0;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          background: ${COLORS.white};
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 999999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          transition: height 0.3s ease, opacity 0.3s ease;
          opacity: 0;
          pointer-events: none;
        }

        #rd-chatbot-widget.open {
          height: 600px;
          opacity: 1;
          pointer-events: auto;
        }

        #rd-chatbot-widget.mobile {
          width: 100%;
          height: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 16px 16px 0 0;
        }

        #rd-chatbot-widget.mobile.open {
          height: 100vh;
          height: 100dvh;
        }

        /* Header */
        .rd-chat-header {
          padding: 16px;
          background: ${COLORS.white};
          border-bottom: 1px solid ${COLORS.lightGray};
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .rd-chat-title {
          font-size: 16px;
          font-weight: 600;
          color: ${COLORS.black};
          margin: 0;
        }

        .rd-chat-close {
          background: none;
          border: none;
          font-size: 20px;
          color: ${COLORS.darkGray};
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .rd-chat-close:hover {
          color: ${COLORS.black};
        }

        /* Messages Container */
        .rd-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: ${COLORS.white};
        }

        .rd-chat-messages::-webkit-scrollbar {
          width: 6px;
        }

        .rd-chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }

        .rd-chat-messages::-webkit-scrollbar-thumb {
          background: ${COLORS.lightGray};
          border-radius: 3px;
        }

        .rd-chat-messages::-webkit-scrollbar-thumb:hover {
          background: ${COLORS.darkGray};
        }

        /* View History Link */
        .rd-chat-view-history {
          text-align: center;
          padding: 8px;
          font-size: 12px;
          color: ${COLORS.darkGray};
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .rd-chat-view-history:hover {
          color: ${COLORS.black};
        }

        /* Message Styles */
        .rd-message {
          display: flex;
          gap: 8px;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .rd-message.user {
          justify-content: flex-end;
        }

        .rd-message-content {
          max-width: 75%;
          padding: 12px;
          border-radius: 12px;
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.4;
          font-size: 14px;
        }

        .rd-message-content a {
          color: inherit;
          text-decoration: underline;
          word-break: break-all;
        }

        .rd-message.bot .rd-message-content {
          background: ${COLORS.offWhite};
          color: ${COLORS.black};
        }

        .rd-message.user .rd-message-content {
          background: ${COLORS.black};
          color: ${COLORS.white};
        }

        /* Typing Indicator */
        .rd-typing-indicator {
          display: flex;
          gap: 4px;
          padding: 12px;
          background: ${COLORS.offWhite};
          border-radius: 12px;
          width: fit-content;
        }

        .rd-typing-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: ${COLORS.darkGray};
          animation: typing 1.4s infinite;
        }

        .rd-typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }

        .rd-typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes typing {
          0%, 60%, 100% {
            opacity: 0.3;
            transform: translateY(0);
          }
          30% {
            opacity: 1;
            transform: translateY(-8px);
          }
        }

        /* Quick Reply Chips */
        .rd-quick-replies {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 8px;
        }

        .rd-chip {
          padding: 8px 12px;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 20px;
          background: ${COLORS.white};
          color: ${COLORS.black};
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .rd-chip:hover {
          background: ${COLORS.offWhite};
          border-color: ${COLORS.black};
        }

        .rd-chip:active {
          transform: scale(0.98);
        }

        /* Input Area */
        .rd-chat-input-area {
          padding: 12px;
          border-top: 1px solid ${COLORS.lightGray};
          display: flex;
          gap: 8px;
          flex-shrink: 0;
          background: ${COLORS.white};
        }

        .rd-input-field {
          flex: 1;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 16px;
          font-family: inherit;
          transition: border-color 0.2s;
          -webkit-appearance: none;
        }

        .rd-input-field:focus {
          outline: none;
          border-color: ${COLORS.black};
        }

        .rd-send-button {
          background: ${COLORS.black};
          border: none;
          color: ${COLORS.white};
          padding: 10px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
          flex-shrink: 0;
        }

        .rd-send-button:hover {
          background: ${COLORS.darkGray};
        }

        .rd-send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Chat Bubble Toggle */
        #rd-chatbot-bubble {
          position: fixed;
          bottom: 20px;
          right: 20px;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: ${COLORS.black};
          color: #FFFFFF;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transition: all 0.3s;
          z-index: 999998;
          padding: 0;
        }

        #rd-chatbot-bubble:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
        }

        #rd-chatbot-bubble.open {
          opacity: 0;
          pointer-events: none;
        }

        /* Unread Badge */
        .rd-unread-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #FF4444;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
        }

        /* Toast/Nudge */
        .rd-toast {
          position: fixed;
          bottom: 90px;
          right: 20px;
          background: ${COLORS.black};
          color: ${COLORS.white};
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 13px;
          z-index: 999997;
          animation: toastIn 0.3s ease;
          max-width: 300px;
        }

        @keyframes toastIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Modal for Email Capture */
        .rd-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 1000000;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .rd-modal-content {
          background: ${COLORS.white};
          padding: 20px;
          border-radius: 16px 16px 0 0;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.15);
        }

        .rd-modal-title {
          font-size: 16px;
          font-weight: 600;
          color: ${COLORS.black};
          margin-bottom: 8px;
        }

        .rd-modal-text {
          font-size: 13px;
          color: ${COLORS.darkGray};
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .rd-modal-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid ${COLORS.lightGray};
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 12px;
          box-sizing: border-box;
        }

        .rd-modal-buttons {
          display: flex;
          gap: 8px;
        }

        .rd-modal-button {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rd-modal-button.primary {
          background: ${COLORS.black};
          color: ${COLORS.white};
        }

        .rd-modal-button.primary:hover {
          background: ${COLORS.darkGray};
        }

        .rd-modal-button.secondary {
          background: ${COLORS.offWhite};
          color: ${COLORS.black};
          border: 1px solid ${COLORS.lightGray};
        }

        .rd-modal-button.secondary:hover {
          background: ${COLORS.lightGray};
        }

        /* Mobile Responsive */
        @media (max-width: 480px) {
          #rd-chatbot-widget {
            width: 100%;
            max-width: 100vw;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 16px 16px 0 0;
          }

          #rd-chatbot-widget.open {
            height: 100vh;
            height: 100dvh;
          }

          .rd-chat-messages {
            padding: 12px;
          }

          .rd-message-content {
            max-width: 85%;
            font-size: 15px;
            padding: 10px;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .rd-message-content a {
            word-break: break-all;
          }

          .rd-chip {
            padding: 10px 14px;
            font-size: 14px;
          }

          .rd-quick-replies {
            flex-wrap: wrap;
          }

          #rd-chatbot-bubble {
            width: 56px;
            height: 56px;
            bottom: 16px;
            right: 16px;
          }

          .rd-toast {
            bottom: 70px;
            right: 16px;
            left: 16px;
            max-width: none;
          }

          .rd-chat-input-area {
            padding: 10px;
            padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
          }

          .rd-input-field {
            font-size: 16px;
          }
        }

        /* iPhone 13 Mini and smaller (375px and below) */
        @media (max-width: 375px) {
          .rd-chat-header {
            padding: 12px;
          }

          .rd-chat-messages {
            padding: 10px;
            gap: 10px;
          }

          .rd-message-content {
            max-width: 88%;
            font-size: 14px;
            padding: 9px;
          }

          .rd-chip {
            padding: 8px 12px;
            font-size: 13px;
          }

          .rd-send-button {
            padding: 10px 12px;
          }
        }
      `;

      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = css;
      document.head.appendChild(style);
    },

    /**
     * Create SVG wave icon
     */
    createWaveIcon() {
      return `
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <path d="M9 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
          <path d="M12 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
          <path d="M15 10h.01" stroke="#FFFFFF" fill="#FFFFFF" opacity="0.6"></path>
        </svg>
      `;
    },

    /**
     * Create main widget DOM
     */
    createWidget() {
      // Create main widget container
      const widget = document.createElement('div');
      widget.id = 'rd-chatbot-widget';
      widget.setAttribute('role', 'dialog');
      widget.setAttribute('aria-label', 'Real Dads Chat');

      widget.innerHTML = `
        <div class="rd-chat-header">
          <h1 class="rd-chat-title">${CONFIG.brandName}</h1>
          <button class="rd-chat-close" aria-label="Close chat" title="Close">
            <span>✕</span>
          </button>
        </div>
        <div class="rd-chat-messages" role="log" aria-live="polite" aria-atomic="false"></div>
        <div class="rd-chat-input-area">
          <input
            type="text"
            class="rd-input-field"
            placeholder="Message..."
            aria-label="Chat message input"
          />
          <button class="rd-send-button" aria-label="Send message" title="Send">
            Send
          </button>
        </div>
      `;

      // Create bubble toggle
      const bubble = document.createElement('button');
      bubble.id = 'rd-chatbot-bubble';
      bubble.setAttribute('aria-label', 'Open chat');
      bubble.setAttribute('aria-expanded', 'false');
      bubble.innerHTML = this.createWaveIcon();

      // Append to page
      document.body.appendChild(widget);
      document.body.appendChild(bubble);

      return { widget, bubble };
    }
  };

  // ============================================================================
  // MESSAGE HANDLING
  // ============================================================================

  const Messages = {
    /**
     * Add message to UI and state
     */
    add(text, isUser = false, chips = []) {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      const messageDiv = document.createElement('div');
      messageDiv.className = `rd-message ${isUser ? 'user' : 'bot'}`;

      const content = document.createElement('div');
      content.className = 'rd-message-content';
      if (isUser) {
        content.textContent = Utils.sanitizeText(text);
      } else {
        // Bot messages: sanitize then linkify URLs
        content.innerHTML = Utils.linkify(Utils.sanitizeText(text));
      }

      messageDiv.appendChild(content);
      messagesContainer.appendChild(messageDiv);

      // Add chips if provided
      if (chips.length > 0 && !isUser) {
        const chipsDiv = document.createElement('div');
        chipsDiv.className = 'rd-quick-replies';

        chips.forEach(chip => {
          const chipBtn = document.createElement('button');
          chipBtn.className = 'rd-chip';
          chipBtn.textContent = chip.label;
          chipBtn.onclick = () => {
            Chat.handleMessage(chip.label);
          };
          chipsDiv.appendChild(chipBtn);
        });

        messageDiv.appendChild(chipsDiv);
      }

      // Store in state
      STATE.messages.push({
        text: text,
        isUser: isUser,
        timestamp: Date.now()
      });

      // Auto-scroll to bottom
      messagesContainer.scrollTop = messagesContainer.scrollHeight;

      // Show history link if needed
      Messages.updateHistoryLink();
    },

    /**
     * Show typing indicator
     */
    showTyping() {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      const typing = document.createElement('div');
      typing.className = 'rd-message bot';
      typing.id = 'rd-typing-indicator';

      const indicator = document.createElement('div');
      indicator.className = 'rd-typing-indicator';
      indicator.innerHTML = '<div class="rd-typing-dot"></div><div class="rd-typing-dot"></div><div class="rd-typing-dot"></div>';

      typing.appendChild(indicator);
      messagesContainer.appendChild(typing);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    },

    /**
     * Remove typing indicator
     */
    hideTyping() {
      const typing = document.getElementById('rd-typing-indicator');
      if (typing) typing.remove();
    },

    /**
     * Update history link visibility
     */
    updateHistoryLink() {
      const messagesContainer = document.querySelector('.rd-chat-messages');
      if (!messagesContainer) return;

      let historyLink = messagesContainer.querySelector('.rd-chat-view-history');
      const messageCount = STATE.messages.length;

      if (messageCount > CONFIG.maxHistoryVisible && !historyLink) {
        historyLink = document.createElement('div');
        historyLink.className = 'rd-chat-view-history';
        historyLink.textContent = 'View earlier messages';
        historyLink.onclick = () => Messages.showFullHistory();
        messagesContainer.insertBefore(historyLink, messagesContainer.firstChild);
      }
    },

    /**
     * Show full history (expand from max visible)
     */
    showFullHistory() {
      const link = document.querySelector('.rd-chat-view-history');
      if (link) link.style.display = 'none';
    }
  };

  // ============================================================================
  // CHAT LOGIC
  // ============================================================================

  const Chat = {
    /**
     * Handle incoming user message
     */
    handleMessage(userMessage) {
      if (!userMessage.trim()) return;

      STATE.messageCount++;
      Analytics.log('message_sent', { messageCount: STATE.messageCount });

      // Show user message
      Messages.add(userMessage, true);

      // Show typing indicator
      Messages.showTyping();

      // Detect intent and generate response
      const intent = Utils.detectIntent(userMessage);
      Analytics.log('intent_detected', { intent: intent });

      // Delay response slightly for natural feel
      const delay = 600 + Math.random() * 600;
      setTimeout(async () => {
        Messages.hideTyping();
        const response = await Chat.generateResponse(userMessage, intent);

        if (response.text) {
          Messages.add(response.text, false, response.chips || []);
        }

        Utils.saveState();
      }, delay);
    },

    /**
     * Generate response based on intent
     */
    async generateResponse(message, intent) {
      const msg = message.toLowerCase();

      switch (intent) {
        case INTENTS.PRODUCT_INQUIRY:
          return Chat.handleProductInquiry(message);

        case INTENTS.NEW_ARRIVALS:
          return Chat.handleNewArrivals();

        case INTENTS.ORDER_STATUS:
          return Chat.handleOrderStatus(message);

        case INTENTS.CUSTOMER_SERVICE:
          return Chat.handleCustomerService(message);

        case INTENTS.WHOLESALE:
          Analytics.log('wholesale_inquiry');
          return {
            text: "For wholesale orders, we handle that through our Faire account. Here's where you can get set up: " + CONFIG.wholesaleUrl,
            chips: [
              { label: 'Shop retail', action: 'navigate-shop' },
              { label: 'Dad joke', action: 'joke' }
            ]
          };

        case INTENTS.BRAND_QUESTION:
          return {
            text: "Real Dads is about showing up — on the court and at home. The Tennis Club collection builds on that idea through clean, everyday pieces inspired by the game.",
            chips: [
              { label: 'Shop the collection', action: 'navigate-shop' },
              { label: 'Dad joke', action: 'joke' }
            ]
          };

        case INTENTS.DAD_JOKE:
          return Chat.handleJoke();

        case INTENTS.GREETING:
          return {
            text: "Hey — welcome in. What can I help you with?",
            chips: [
              { label: 'Track my order', action: 'order-status' },
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Returns and exchanges', action: 'returns' }
            ]
          };

        case INTENTS.SIZING:
          return {
            text: "For the best fit info, check the size guide on each product page. Our pieces are designed with a relaxed, comfortable fit in mind.",
            chips: [
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Back to questions', action: 'help' }
            ]
          };

        case INTENTS.LEAD_CAPTURE:
          return Chat.handleLeadCapture();

        case INTENTS.THANKS:
          return {
            text: Utils.random([
              "Appreciate you stopping by.",
              "Take care.",
              "Anytime. See you soon."
            ]),
            chips: [
              { label: 'Dad joke', action: 'joke' },
              { label: 'Back to shopping', action: 'shop' }
            ]
          };

        case INTENTS.NAVIGATION:
          return Chat.handleNavigation(msg);

        default:
          return {
            text: "I can help with products, customer service, order questions, sizing, or wholesale. What do you need?",
            chips: [
              { label: 'Track my order', action: 'order-status' },
              { label: 'Returns and exchanges', action: 'returns' },
              { label: 'Shop the collection', action: 'shop' },
              { label: 'Wholesale info', action: 'wholesale' }
            ]
          };
      }
    },

    /**
     * Return the current free-shipping copy, using live cart data when available.
     */
    async getFreeShippingLine(preferLiveGap = false) {
      const cart = await CartService.fetch();
      const status = Utils.getFreeShippingStatus(CartService.getSubtotalCents(cart));

      if (status.unlocked) {
        return {
          text: 'You already unlocked free shipping.',
          status
        };
      }

      if (preferLiveGap && status.subtotalCents > 0) {
        return {
          text: `Free shipping unlocks at ${status.thresholdLabel}, and you're ${status.remainingLabel} away right now.`,
          status
        };
      }

      return {
        text: `Free shipping unlocks at ${status.thresholdLabel}.`,
        status
      };
    },

    /**
     * Return shirt-specific BOGO copy when that offer applies.
     */
    getBogoLine(shouldSurface) {
      if (!shouldSurface || !CONFIG.bogoEnabled) return '';
      Analytics.log('bogo_surfaced');
      return `Heads up — ${CONFIG.bogoLabel}.`;
    },

    /**
     * Return a "complete the look" cross-sell line for the matched product.
     */
    getCrossSellLine(product) {
      if (!product) return '';
      const url = Offers.getCompleteTheLookUrl(product);
      Analytics.log('bundle_cross_sell', {
        product_name: product.name || '',
        destination: url
      });
      if (Offers.isShirtProduct(product)) {
        return `If you want to complete the look, browse the caps here: ${url}`;
      }
      return `If you want the full look, browse the collection here: ${url}`;
    },

    /**
     * Handle order status and tracking questions.
     */
    handleOrderStatus(message) {
      Analytics.log('order_status_inquiry');

      const tracking = Utils.detectTrackingInfo(message);
      if (tracking) {
        return {
          text: `That looks like a ${tracking.carrier} tracking number. Track it here: ${tracking.trackingUrl}`,
          chips: [
            { label: 'Returns and exchanges', action: 'returns' },
            { label: 'Contact support', action: 'contact' },
            { label: 'Shop the collection', action: 'shop' }
          ]
        };
      }

      const orderNumber = Utils.extractOrderNumber(message);
      const email = Utils.extractEmail(message);
      const supportDetails = [orderNumber, email].filter(Boolean).join(' and ');

      if (CONFIG.customerLoggedIn && NAVIGATION.account.url) {
        return {
          text: `You can check your latest order updates in your account here: ${NAVIGATION.account.url}. If you already have a tracking number, paste it here and I’ll turn it into a direct carrier link.${supportDetails ? ` If you still need help, contact us with ${supportDetails}: ${NAVIGATION.contact.url}` : ` If you still need help, contact us here: ${NAVIGATION.contact.url}`}`,
          chips: [
            { label: 'Paste a tracking number', action: 'tracking' },
            { label: 'Contact support', action: 'contact' },
            { label: 'Returns and exchanges', action: 'returns' }
          ]
        };
      }

      const accountGuidance = CONFIG.customerAccountsEnabled && NAVIGATION.account_login.url
        ? `If you have an account, sign in here to review your order history: ${NAVIGATION.account_login.url}. `
        : '';

      const supportGuidance = supportDetails
        ? `For a manual lookup, contact us with ${supportDetails}: ${NAVIGATION.contact.url}`
        : `For a manual lookup, contact us with your order number and checkout email here: ${NAVIGATION.contact.url}`;

      return {
        text: `${accountGuidance}If you already received a shipping confirmation, the fastest option is the tracking link in that email. You can also paste a tracking number here and I’ll turn it into a direct carrier link. ${supportGuidance}`,
        chips: [
          { label: 'I have a tracking number', action: 'tracking' },
          { label: 'Contact support', action: 'contact' },
          { label: 'Returns and exchanges', action: 'returns' }
        ]
      };
    },

    /**
     * Handle broader customer service questions.
     */
    async handleCustomerService(message) {
      Analytics.log('customer_service_inquiry');

      const msg = message.toLowerCase();
      const orderNumber = Utils.extractOrderNumber(message);
      const prefix = orderNumber ? `For order ${orderNumber}, ` : '';

      if (/\b(return|returns|exchange|exchanges|refund|refunds)\b/.test(msg)) {
        return {
          text: `${prefix}the best next step is our support flow here: ${NAVIGATION.contact.url}. You can also check the FAQ here: ${NAVIGATION.faq.url}. Include your order number, the item, and what you want to change so the team can move faster.`,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Contact support', action: 'contact' },
            { label: 'Shop the collection', action: 'shop' }
          ]
        };
      }

      if (/\b(cancel|cancellation|change address|address change)\b/.test(msg)) {
        return {
          text: `${prefix}please contact support as soon as possible here: ${NAVIGATION.contact.url}. Share your order number and the change you need, and the team can try to catch it before shipment.`,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Contact support', action: 'contact' }
          ]
        };
      }

      if (/\b(wrong item|damaged|defective|delivery issue)\b/.test(msg)) {
        return {
          text: `${prefix}please contact support here: ${NAVIGATION.contact.url}. Include your order number and a quick note about what arrived so the team can sort it out.`,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Returns and exchanges', action: 'returns' }
          ]
        };
      }

      if (/\b(shipping question|shipping|delivery)\b/.test(msg)) {
        const freeShip = await Chat.getFreeShippingLine(true);
        return {
          text: `${prefix}shipping updates usually come from your shipping confirmation email. ${freeShip.text} If you have a tracking number, paste it here and I’ll build the direct carrier link. If you need more help, use the contact page: ${NAVIGATION.contact.url}`,
          chips: [
            { label: 'I have a tracking number', action: 'tracking' },
            { label: 'Track my order', action: 'order-status' },
            { label: 'Show me caps', action: 'shop' }
          ]
        };
      }

      return {
        text: `I can help with order status, shipping questions, returns, exchanges, or general support. If you need the team, use the contact page here: ${NAVIGATION.contact.url}`,
        chips: [
          { label: 'Track my order', action: 'order-status' },
          { label: 'Returns and exchanges', action: 'returns' },
          { label: 'Contact support', action: 'contact' }
        ]
      };
    },

    /**
     * Handle product inquiry
     */
    async handleProductInquiry(message) {
      const msg = message.toLowerCase();
      const matched = Chat.scoreProducts(msg);
      const leadMatch = matched[0];
      const shouldSurfaceBogo = CONFIG.bogoEnabled && (/\b(shirt|shirts|tee|tees|t-shirt)\b/.test(msg) || Offers.isShirtProduct(leadMatch));
      const bogoLine = Chat.getBogoLine(shouldSurfaceBogo);
      const freeShip = await Chat.getFreeShippingLine(true);
      const crossSellLine = leadMatch ? Chat.getCrossSellLine(leadMatch) : '';

      // Check for pricing questions first — never guess prices
      if (/\b(price|pricing|cost|how much|per unit|\$)\b/.test(msg)) {
        if (leadMatch) {
          const detailLines = [freeShip.text];
          if (bogoLine) detailLines.push(bogoLine);
          if (crossSellLine) detailLines.push(crossSellLine);
          return {
            text: `Pricing is on the product page — here's the link: ${leadMatch.url}\n\n${detailLines.join(' ')}`,
            chips: [
              { label: 'Show me caps', action: 'shop' },
              { label: 'Free shipping info', action: 'shipping info' },
              { label: 'Wholesale pricing', action: 'wholesale' }
            ]
          };
        }
        return {
          text: `Pricing is on each product page. ${freeShip.text} Check the collection here: ` + (NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl),
          chips: [
            { label: 'Shop the collection', action: 'shop' },
            { label: 'Show me caps', action: 'shop' },
            { label: 'Wholesale pricing', action: 'wholesale' }
          ]
        };
      }

      if (matched.length === 1) {
        const p = leadMatch;
        const detailLines = [];
        if (bogoLine) detailLines.push(bogoLine);
        detailLines.push(freeShip.text);
        if (crossSellLine) detailLines.push(crossSellLine);
        return {
          text: `${p.name} — ${p.description} Check it out here: ${p.url}${detailLines.length ? `\n\n${detailLines.join(' ')}` : ''}`,
          chips: [
            { label: Offers.isShirtProduct(p) ? 'Show me caps' : 'Show me tees', action: 'shop' },
            { label: 'Free shipping info', action: 'shipping info' },
            { label: 'Wholesale info', action: 'wholesale' },
            { label: 'See more products', action: 'shop' }
          ]
        };
      }

      if (matched.length > 1) {
        let text = "Here's what I'd point you to:\n";
        matched.slice(0, 4).forEach(p => {
          text += `\n${p.name} — ${p.url}`;
        });
        if (matched.length > 4) {
          text += `\n\n...and ${matched.length - 4} more in the collection.`;
        }
        if (bogoLine) text += `\n\n${bogoLine}`;
        text += `\n\n${freeShip.text}`;
        return {
          text: text.trim(),
          chips: [
            { label: 'See full collection', action: 'shop' },
            { label: 'Show me caps', action: 'shop' },
            { label: 'Free shipping info', action: 'shipping info' }
          ]
        };
      }

      // General collection inquiry
      if (/\b(collection|tennis club|what do you (sell|have)|show me|everything)\b/.test(msg)) {
        const collectionUrl = NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl;
        return {
          text: `The Tennis Club collection is our current lineup — clean, everyday pieces inspired by the game. ${freeShip.text} Browse it all here: ${collectionUrl}`,
          chips: [
            { label: 'Show me caps', action: 'shop' },
            { label: 'Tell me about the brand', action: 'brand' },
            { label: 'Free shipping info', action: 'shipping info' }
          ]
        };
      }

      return {
        text: `I don't want to point you to something we don't have. ${freeShip.text} Check out the full collection here: ` + (NAVIGATION.tennis_club ? NAVIGATION.tennis_club.url : CONFIG.websiteUrl),
        chips: [
          { label: 'View collection', action: 'shop' },
          { label: 'Show me caps', action: 'shop' },
          { label: 'Wholesale', action: 'wholesale' }
        ]
      };
    },

    /**
     * Handle new arrivals request — show products tagged "new"
     */
    handleNewArrivals() {
      Analytics.log('new_arrivals_inquiry');

      const newProducts = STATE.products.filter(p => {
        const tags = (p.tags || []).map(t => t.toLowerCase());
        return tags.includes('new');
      });

      if (newProducts.length === 0) {
        return {
          text: "Check out the latest drops in the collection: " + CONFIG.websiteUrl + "/collections/all",
          chips: [
            { label: 'Shop all', action: 'shop' },
            { label: 'Dad joke', action: 'joke' }
          ]
        };
      }

      let text = "Just dropped — here's what's new:\n";
      newProducts.slice(0, 5).forEach(p => {
        text += `\n${p.name} — ${p.url}`;
      });
      if (newProducts.length > 5) {
        text += `\n\n...plus ${newProducts.length - 5} more. Browse them all at ${CONFIG.websiteUrl}/collections/all`;
      }

      return {
        text: text.trim(),
        chips: [
          { label: 'Shop all products', action: 'shop' },
          { label: 'Wholesale info', action: 'wholesale' },
          { label: 'Dad joke', action: 'joke' }
        ]
      };
    },

    /**
     * Score products against user message using weighted tag matching
     */
    scoreProducts(msg) {
      if (STATE.products.length === 0) return [];

      // Expand synonyms so "hoodie" matches "crewneck/sweatshirt" etc.
      const synonyms = {
        hoodie: ['crewneck', 'sweatshirt'],
        hoodies: ['crewneck', 'sweatshirt'],
        sweater: ['crewneck', 'sweatshirt'],
        tee: ['t-shirt', 'shirt'],
        cap: ['hat'],
        snapback: ['hat', 'cap']
      };
      let expandedMsg = msg;
      Object.keys(synonyms).forEach(term => {
        if (msg.includes(term)) {
          expandedMsg += ' ' + synonyms[term].join(' ');
        }
      });

      const scored = STATE.products.map(product => {
        let score = 0;
        const name = (product.name || '').toLowerCase();
        const desc = (product.description || '').toLowerCase();
        const category = (product.category || '').toLowerCase();
        const tags = (product.tags || []).map(t => t.toLowerCase());

        // Direct name match (highest weight)
        if (expandedMsg.includes(name)) score += 10;

        // Category match
        if (expandedMsg.includes(category)) score += 5;

        // Tag matches
        tags.forEach(tag => {
          if (expandedMsg.includes(tag)) score += 3;
        });

        // Keyword matches in name
        const words = expandedMsg.split(/\s+/).filter(w => w.length > 2);
        words.forEach(word => {
          if (name.includes(word)) score += 4;
          if (desc.includes(word)) score += 1;
        });

        return { ...product, score };
      });

      return scored
        .filter(p => p.score > 0)
        .sort((a, b) => b.score - a.score);
    },

    /**
     * Handle dad joke request
     */
    handleJoke() {
      Analytics.log('dad_joke_requested');

      if (STATE.jokes.length === 0) {
        return {
          text: "I've got jokes, but they're still loading. Check back in a sec!",
          chips: [
            { label: 'Shop', action: 'shop' },
            { label: 'About Real Dads', action: 'brand' }
          ]
        };
      }

      // Get random unused joke
      let joke = null;
      const available = STATE.jokes.filter(j => !STATE.usedJokes.has(j.id || j.setup));

      if (available.length === 0) {
        // All jokes used, reset and pick random
        STATE.usedJokes.clear();
        joke = STATE.jokes[Math.floor(Math.random() * STATE.jokes.length)];
      } else {
        joke = available[Math.floor(Math.random() * available.length)];
      }
      STATE.usedJokes.add(joke.id || joke.setup);

      // Return joke setup first
      const setupText = joke.setup || joke.text;
      setTimeout(() => {
        Messages.add("...", false);
        setTimeout(() => {
          // Replace ... with punchline
          const messagesContainer = document.querySelector('.rd-chat-messages');
          const lastMsg = messagesContainer.lastChild;
          if (lastMsg && lastMsg.querySelector('.rd-message-content')) {
            lastMsg.querySelector('.rd-message-content').textContent = joke.punchline || joke.text;
            lastMsg.parentElement.appendChild(
              (() => {
                const chipsDiv = document.createElement('div');
                chipsDiv.className = 'rd-quick-replies';

                const chip1 = document.createElement('button');
                chip1.className = 'rd-chip';
                chip1.textContent = 'Another one';
                chip1.onclick = () => {
                  Chat.handleMessage('another joke');
                };

                const chip2 = document.createElement('button');
                chip2.className = 'rd-chip';
                chip2.textContent = 'Back to shopping';
                chip2.onclick = () => {
                  Chat.handleMessage('show me products');
                };

                chipsDiv.appendChild(chip1);
                chipsDiv.appendChild(chip2);
                return chipsDiv;
              })()
            );
          }
        }, 800);
      }, 800);

      return {
        text: setupText,
        chips: []
      };
    },

    /**
     * Handle lead capture
     */
    handleLeadCapture() {
      // Show email modal
      const overlay = document.createElement('div');
      overlay.className = 'rd-modal-overlay';

      const content = document.createElement('div');
      content.className = 'rd-modal-content';
      content.innerHTML = `
        <div class="rd-modal-title">Stay in the loop</div>
        <div class="rd-modal-text">Get notified about new drops and releases.</div>
        <input type="email" class="rd-modal-input" placeholder="your@email.com" />
        <div class="rd-modal-buttons">
          <button class="rd-modal-button secondary" onclick="this.closest('.rd-modal-overlay').remove()">
            Maybe later
          </button>
          <button class="rd-modal-button primary">Subscribe</button>
        </div>
      `;

      overlay.appendChild(content);
      document.body.appendChild(overlay);

      // Handle subscribe
      const submitBtn = content.querySelector('.rd-modal-button.primary');
      const input = content.querySelector('.rd-modal-input');

      submitBtn.onclick = () => {
        const email = input.value.trim();
        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          // Store email
          let emails = JSON.parse(localStorage.getItem('rd_chat_emails') || '[]');
          if (!emails.includes(email)) {
            emails.push(email);
            localStorage.setItem('rd_chat_emails', JSON.stringify(emails));
          }

          Analytics.log('lead_captured', { email: email });

          overlay.remove();
          Messages.add("Got it — we'll keep you in the loop.", false, [
            { label: 'Back to shopping', action: 'shop' }
          ]);
        } else {
          input.style.borderColor = '#FF4444';
        }
      };

      return {
        text: "I'll show you a form to sign up.",
        chips: []
      };
    },

    /**
     * Handle navigation
     */
    handleNavigation(msg) {
      if (msg.includes('about')) {
        return {
          text: "Learn more about the brand here: " + NAVIGATION.about.url,
          chips: [
            { label: 'Shop', action: 'shop' },
            { label: 'Wholesale', action: 'wholesale' }
          ]
        };
      }

      if (msg.includes('contact')) {
        return {
          text: "Get in touch here: " + NAVIGATION.contact.url,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Returns and exchanges', action: 'returns' }
          ]
        };
      }

      if (msg.includes('faq')) {
        return {
          text: "You can find the FAQ here: " + NAVIGATION.faq.url,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Contact support', action: 'contact' }
          ]
        };
      }

      if (msg.includes('account') || msg.includes('login')) {
        const accountUrl = CONFIG.customerLoggedIn ? NAVIGATION.account.url : NAVIGATION.account_login.url;
        return {
          text: "Head here for your account: " + accountUrl,
          chips: [
            { label: 'Track my order', action: 'order-status' },
            { label: 'Contact support', action: 'contact' }
          ]
        };
      }

      return {
        text: "Check out the collection: " + CONFIG.websiteUrl,
        chips: [
          { label: 'Track my order', action: 'order-status' },
          { label: 'About', action: 'about' },
          { label: 'Wholesale', action: 'wholesale' }
        ]
      };
    }
  };

  // ============================================================================
  // UI INTERACTION
  // ============================================================================

  const UI = {
    /**
     * Initialize widget interactions
     */
    init(elements) {
      const { widget, bubble } = elements;

      // Toggle open/close
      bubble.addEventListener('click', () => {
        UI.toggle(widget, bubble);
      });

      const closeBtn = widget.querySelector('.rd-chat-close');
      closeBtn.addEventListener('click', () => {
        UI.close(widget, bubble);
      });

      // Handle input
      const input = widget.querySelector('.rd-input-field');
      const sendBtn = widget.querySelector('.rd-send-button');

      const sendMessage = () => {
        const message = input.value.trim();
        if (message) {
          Chat.handleMessage(message);
          input.value = '';
          input.focus();
        }
      };

      sendBtn.addEventListener('click', sendMessage);
      input.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          sendMessage();
        }
      });

      // Handle mobile
      UI.detectMobile(widget);
      window.addEventListener('resize', () => UI.detectMobile(widget));
    },

    /**
     * Toggle widget open/close
     */
    toggle(widget, bubble) {
      if (widget.classList.contains('open')) {
        UI.close(widget, bubble);
      } else {
        UI.open(widget, bubble);
      }
    },

    /**
     * Open widget
     */
    open(widget, bubble) {
      widget.classList.add('open');
      bubble.classList.add('open');
      bubble.setAttribute('aria-expanded', 'true');
      STATE.isOpen = true;
      STATE.hasShownNudge = true;
      Analytics.log('widget_opened');
    },

    /**
     * Close widget
     */
    close(widget, bubble) {
      widget.classList.remove('open');
      bubble.classList.remove('open');
      bubble.setAttribute('aria-expanded', 'false');
      STATE.isOpen = false;
      Analytics.log('widget_closed');
    },

    /**
     * Detect mobile and adjust widget
     */
    detectMobile(widget) {
      if (window.innerWidth < 480) {
        widget.classList.add('mobile');
      } else {
        widget.classList.remove('mobile');
      }
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  async function init() {
    // Inject CSS
    DOM.injectStyles();

    // Create DOM
    const elements = DOM.createWidget();
    STATE.ui = elements;

    // Initialize interactions
    UI.init(elements);

    // Load session
    STATE.sessionId = Utils.getOrCreateSessionId();
    const hasLoadedState = Utils.loadState();

    // Load external data (products.json has nested structure, jokes.json has { jokes: [] })
    const [productsData, jokesData] = await Promise.all([
      Utils.fetchJson(CONFIG.productsJsonUrl),
      Utils.fetchJson(CONFIG.jokesJsonUrl)
    ]);

    if (productsData) {
      // Extract products from nested collections structure
      if (productsData.collections) {
        const allProducts = [];
        Object.values(productsData.collections).forEach(collection => {
          if (Array.isArray(collection.products)) {
            collection.products.forEach(p => {
              allProducts.push({ ...p, collectionName: collection.name, collectionUrl: collection.url });
            });
          }
        });
        STATE.products = allProducts;
      } else if (Array.isArray(productsData)) {
        STATE.products = productsData;
      }
      // Also store navigation links
      if (productsData.navigation) {
        Object.assign(NAVIGATION, {
          shop: { label: 'Shop', url: productsData.navigation.shop || CONFIG.websiteUrl },
          collections: { label: 'Collections', url: productsData.navigation.collections || CONFIG.websiteUrl + '/collections' },
          tennis_club: { label: 'Tennis Club', url: productsData.navigation.tennis_club || CONFIG.websiteUrl + '/collections/tennis-club' },
          about: { label: 'About', url: productsData.navigation.about || NAVIGATION.about.url },
          contact: { label: 'Contact', url: productsData.navigation.contact || NAVIGATION.contact.url },
          faq: { label: 'FAQ', url: productsData.navigation.faq || NAVIGATION.faq.url },
        });
      }
      // Store brand info
      if (productsData.brand) {
        STATE.brand = productsData.brand;
      }
    }

    if (jokesData) {
      // Support both { jokes: [] } and raw array formats
      STATE.jokes = Array.isArray(jokesData) ? jokesData : (jokesData.jokes || []);
    }

    // Analytics
    Analytics.log('session_start', {
      isReturning: hasLoadedState,
      sessionId: STATE.sessionId
    });

    // Show first message if new session
    if (!hasLoadedState || STATE.messages.length === 0) {
      STATE.started = true;
      Messages.add("Welcome to Real Dads. Let me know if you need anything.");

      setTimeout(() => {
        Messages.add("Or I can tell you a solid dad joke.", false, [
          { label: 'Track my order', action: 'order-status' },
          { label: 'Returns and exchanges', action: 'returns' },
          { label: 'Show me products', action: 'shop' }
        ]);
      }, 1500);
    } else {
      // Restore previous messages
      const messagesContainer = elements.widget.querySelector('.rd-chat-messages');
      STATE.messages.slice(-CONFIG.maxHistoryVisible).forEach(msg => {
        const messageDiv = document.createElement('div');
        messageDiv.className = `rd-message ${msg.isUser ? 'user' : 'bot'}`;

        const content = document.createElement('div');
        content.className = 'rd-message-content';
        if (msg.isUser) {
          content.textContent = Utils.sanitizeText(msg.text);
        } else {
          content.innerHTML = Utils.linkify(Utils.sanitizeText(msg.text));
        }

        messageDiv.appendChild(content);
        messagesContainer.appendChild(messageDiv);
      });

      Messages.updateHistoryLink();
    }

    Utils.saveState();

    Nudges.init();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
